import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SbSupportAccessWidgetComponent } from '@app/+modals/sb-support/widgets/access/sb-support-access-widget.component';
import { SbSupportAccountWidgetComponent } from '@app/+modals/sb-support/widgets/account/sb-support-account-widget.component';
import { SbChatWidgetComponent } from '@app/+modals/sb-support/widgets/chat/sb-chat-widget.component';
import { SbSupportChecklistWidgetComponent } from '@app/+modals/sb-support/widgets/checklist/sb-support-checklist-widget.component';
import { SbSupportContactWidgetComponent } from '@app/+modals/sb-support/widgets/contact/sb-support-contact-widget.component';
import { SbRemoteSupportWidgetComponent } from '@app/+modals/sb-support/widgets/support/sb-remote-support-widget.component';
import { TrackingService } from '@app/services/tracking.service';
import { HubSpotService } from '@app/startup/hub-spot.service';
import { Store } from '@ngrx/store';
import { getAccount } from '@reducers/account/account-service.selectors';
import { AccountModel } from '@reducers/account/account.model';
import { getPermissionState } from '@reducers/auth/permission.helper';
import { AppState } from '@reducers/interfaces';
import { hasPermission } from '@sb/shared/permission/util';
import { SurfaceDirective } from '@sb/ui/surface';
import { OnboardingChecklistService } from '@shared/onboarding/onboarding-checklist.service';
import { distinctUntilChanged, skip } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'sb-support-content',
  imports: [
    SurfaceDirective,
    SbChatWidgetComponent,
    SbSupportAccessWidgetComponent,
    SbRemoteSupportWidgetComponent,
    SbSupportAccountWidgetComponent,
    SbSupportContactWidgetComponent,
    SbSupportChecklistWidgetComponent,
  ],
  templateUrl: 'sb-support-content.component.html',
  host: {
    class: 'flex flex-col gap-4',
  },
})
export class SbSupportContentComponent {
  #store = inject<Store<AppState>>(Store);
  #hubSpotService = inject(HubSpotService);
  #trackingService = inject(TrackingService);
  #onboardingChecklistService = inject(OnboardingChecklistService);
  #destroyRef = inject(DestroyRef);

  account = signal<AccountModel>(undefined);
  mode = signal<'accountManager' | 'employee'>(undefined);

  supportPhone = computed(() => this.account()?.support_phone || '');
  supportEmail = computed(() => this.account()?.support || '');
  hasConsent: boolean;

  hasOnboardingChecklist = this.#onboardingChecklistService.hasChecklist;

  openOnboarding = output<void>();

  constructor() {
    this.hasConsent = this.#hubSpotService.crmConsent;

    this.#store
      .select(getAccount)
      .pipe(takeUntilDestroyed())
      .subscribe((account) => {
        this.account.set(account);
      });

    this.#store
      .select(getPermissionState)
      .pipe(takeUntilDestroyed())
      .subscribe((permissionState) => {
        const manageAccount = hasPermission(
          {
            permissions: ['Manage account'],
            userId: 'me',
            departments: 'any',
          },
          permissionState,
        );

        this.mode.set(permissionState.isAccountManager || manageAccount ? 'accountManager' : 'employee');
      });

    this.#hubSpotService.isChatEnabled$
      // Prevent from tracking event on init
      // chatToggled (which updates isChatEnabled$) is called on init
      // and isChatEnabled$ is a BehaviorSubject that always emits at least once
      .pipe(distinctUntilChanged(), skip(1), takeUntilDestroyed())
      .subscribe((chat_enabled) => {
        void this.#trackingService.event('Support Chat Toggled', {
          chat_enabled,
        });
      });
  }
  chatToggled(active: boolean) {
    this.#hubSpotService.isChatEnabled$.next(active);
    if (!active) {
      return;
    }
    this.hasConsent = true;
  }

  openOnboardingChecklist() {
    this.#onboardingChecklistService
      .resumeGuide()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.openOnboarding.emit();
      });
  }
}
